/** The compliance checklist panel — **gate 26**.
 *
 * One place that says, for one **filing obligation**, what the regime asks of the company and what
 * is outstanding. It composes the two reads Reports already makes (`ObligationOut` and `ReportOut`)
 * through `checklist.ts`; it fetches nothing and adds no route. See that file for the rules it
 * keeps. What belongs here is only how they look:
 *
 * - **A status is a word on every item.** Colour reinforces it and is never the only signal.
 * - **The regime's caveat comes first and once.** When SB 253's date is proposed, or SB 261 is
 *   enjoined, the line saying so sits above every item, and the deadline item repeats it beside the
 *   date. There is no countdown anywhere on this panel and no clock in its inputs.
 * - **The counts are counts.** There is no "complete", no percentage and no tick for the whole.
 *   The verdict item's strongest status is *For a person*.
 * - **A read that has not arrived is said, per section.** The three sections that need the report
 *   show *Not checked* until it does, so a slow or failed read never renders as a short list.
 */

import { CheckCircle, CircleDashed, Info, Scales, WarningCircle } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";

import type { Obligation, Report } from "../api/queries";
import { Panel } from "../components/Panel";
import { quantity, shortDate } from "../lib/format";
import { buildChecklist, tally } from "./checklist";
import type { Item, Note, SectionId, Status } from "./checklist";
import own from "./ComplianceChecklist.module.css";

function cls(...names: (string | undefined | false)[]): string {
  return names.filter(Boolean).join(" ");
}

/** Section headings are labels, not claims: nouns for what the section holds. */
const HEADING: Record<SectionId, string> = {
  applicability: "Determination",
  deadline: "Filing date",
  disclosures: "Disclosures",
  checks: "Checks",
  assurance: "Assurance",
  standing: "Standing",
};

const STATUS_WORD: Record<Status, string> = {
  met: "Met",
  open: "Open",
  unchecked: "Not checked",
  noted: "Noted",
  person: "For a person",
};

/** A glyph per status so state never depends on colour alone. The word stays beside it. `person` is
 * the scales: the verdict item's strongest status is a person's judgement, never a tick. */
const STATUS_ICON: Record<Status, Icon> = {
  met: CheckCircle,
  open: WarningCircle,
  unchecked: CircleDashed,
  noted: Info,
  person: Scales,
};

const STATUS_CLASS: Record<Status, string | undefined> = {
  met: own.statusMet,
  open: own.statusOpen,
  unchecked: own.statusUnchecked,
  noted: undefined,
  person: own.statusPerson,
};

const NOTE_LABEL: Record<Note["kind"], string> = {
  detail: "Found",
  remedy: "Next step",
  dispute: "Disputed",
  missing: "Missing",
  limitation: "Limitation",
  unavailable: "Not stated",
  first_cycle_year: "First cycle year",
};

/** How an item's raw code reads. Codes are the payload's own tokens; this only un-snakes them. */
function codeText(item: Item): string | null {
  if (item.section === "deadline") return item.code ? shortDate(item.code) : "No date";
  if (item.id === "applicability:superseded") return null;
  if (item.id === "checks:run") return item.code === null ? null : `${item.code} run`;
  if (item.code === null) return null;
  return item.code.replace(/_/g, " ");
}

function figureText(item: Item): string | null {
  if (!item.figure) return null;
  const { value, unit } = item.figure;
  // A reconciliation delta is already a signed decimal string; reformatting would drop the sign.
  const shown = item.section === "checks" ? value : quantity(value, unit === "kWh" ? 0 : 1);
  return unit ? `${shown} ${unit}` : shown;
}

function Row({ item }: { item: Item }) {
  const code = codeText(item);
  const Glyph = STATUS_ICON[item.status];
  const figure = figureText(item);
  return (
    <li
      className={own.item}
      data-testid="checklist-item"
      data-item-id={item.id}
      data-status={item.status}
    >
      <span className={cls(own.status, STATUS_CLASS[item.status])}>
        <Glyph size={14} weight={item.status === "met" ? "fill" : "regular"} aria-hidden="true" />
        {STATUS_WORD[item.status]}
      </span>
      <span className={own.body}>
        <span>
          <span className={own.title}>{item.title}</span>
          {code ? <span className={own.code}>{code}</span> : null}
          {figure ? <span className={cls(own.code, own.figure)}>{figure}</span> : null}
        </span>
        {item.qualifier ? (
          // Beside the date it qualifies: a reader who reached the date has already believed it.
          <span className={own.qualifier} data-testid="checklist-qualifier">
            <span className={own.qualifierTitle}>{item.qualifier.label}</span>
            {item.qualifier.caveat}
          </span>
        ) : null}
        {item.text && item.text !== item.title ? <span className={own.text}>{item.text}</span> : null}
        {item.notes.map((n) => (
          <span key={`${n.kind}:${n.text}`} className={own.note} data-note={n.kind}>
            <span className={own.noteKind}>{NOTE_LABEL[n.kind]}</span>
            {n.text}
          </span>
        ))}
        {item.trace?.citation ? (
          <span className={own.cite}>
            {item.trace.sourceUrl ? (
              <a href={item.trace.sourceUrl} target="_blank" rel="noreferrer">
                {item.trace.citation}
              </a>
            ) : (
              item.trace.citation
            )}
          </span>
        ) : null}
      </span>
    </li>
  );
}

export function ComplianceChecklistPanel({
  obligation,
  report,
}: {
  obligation: Obligation;
  /** `undefined` while loading or after a failed read — the report sections then say *Not checked*. */
  report: Report | undefined;
}) {
  const checklist = buildChecklist(obligation, report);
  const counts = tally(checklist);
  return (
    <Panel
      title="Compliance checklist"
      sub={`${obligation.regime_name} · ${obligation.legal_entity_name} · year ending ${shortDate(obligation.financial_year_end)} · rules version ${obligation.regime_version}`}
      column="1 / 13"
    >
      <div data-testid="compliance-checklist" data-owed={checklist.owed}>
        {checklist.inForce ? (
          <p className={own.inForce} data-testid="checklist-in-force">
            <span className={own.qualifierTitle}>{checklist.inForce.label}</span>
            {checklist.inForce.caveat}
          </p>
        ) : null}
        <ul className={own.tally} data-testid="checklist-tally" aria-label="Counts by status">
          {(Object.keys(STATUS_WORD) as Status[]).map((status) => (
            <li key={status} data-status={status}>
              <strong className="num">{counts[status]}</strong> {STATUS_WORD[status].toLowerCase()}
            </li>
          ))}
        </ul>
        <div className={own.sections}>
          {checklist.sections.map((section) => (
            <section key={section.id} className={own.section} data-section={section.id}>
              <h3 className={own.sectionHead}>{HEADING[section.id]}</h3>
              <ul className={own.items}>
                {section.items.map((item) => (
                  <Row key={item.id} item={item} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </Panel>
  );
}
